import { AfterViewInit, Component, Input, OnChanges, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ChipsAutoCompleteComponent } from '@app/components/platform';
import { Chippable } from '@app/interfaces/chippable.interface';
import { CompanyDocument } from '@app/models/documents/company-document.model';
import { DocumentTag } from '@app/models/documents/document-tag.model';
import { BaseForm } from '@forms/base.form';
import { FeatureService } from '@app/services/feature.service';
import { FeatureFlag } from '@app/enums';
import { TranslatableKey } from '@app/types/translatable.type';

@Component({
    selector: 'app-company-document-form',
    styleUrls: ['./company-document.style.scss'],
    templateUrl: './company-document.template.html',
})
export class CompanyDocumentForm extends BaseForm implements OnChanges, AfterViewInit {
    @ViewChild('tagAutocomplete', { static: true }) tagAutocomplete!: ChipsAutoCompleteComponent;
    @Input() set companyDocument(companyDocument: CompanyDocument) {
        this._companyDocument = companyDocument;
        // If a file is provided for editing, rather than creating, we don't need the file form control to be part of form validation
        if (companyDocument.fileId) {
            this.form.form.removeControl('file');
        }
    }
    get companyDocument(): CompanyDocument {
        return this._companyDocument;
    }
    @Input() allTags: DocumentTag[] = [];
    @Input() showNumberOfCosigner = true;
    @Input() showPublicCheckbox = true;

    _companyDocument: CompanyDocument = new CompanyDocument();
    cosigners = 0;
    file = new FormControl<File | null>(null, { validators: Validators.required });
    cosignersLabel: { key: TranslatableKey; count: number } = { key: '', count: 0 };
    fileName = '';
    showCosigners = false;

    constructor(private features: FeatureService) {
        super();
    }

    async ngOnInit(): Promise<void> {
        if (await this.features.has(FeatureFlag.documentsV2)) {
            // Using checkbox to toggle document between public and private is deprecated
            this.showPublicCheckbox = false;
        }
    }

    ngOnChanges(): void {
        this.setCosignersLabel();
        this.showCosigners = this.companyDocument.isSigningDocument() && this.showNumberOfCosigner;
    }

    ngAfterViewInit(): void {
        // We need to add the control after the view has initialized because otherwise the form doesn't exist.
        // However adding it immediately after the view is initialized will cause a ChangedAfterChecked error due to Angular's change detection cycles
        // Reserving it for the next change detection cycle avoids this error.
        setTimeout(() => {
            this.form.form.addControl('file', this.file);
        });
    }

    reset(): void {
        super.reset();
        this.tagAutocomplete.clearAll();
        this.file.setValue(null);
    }

    onFileSelected(files: FileList): void {
        this.file.setValue(files[0]);
        if (this.file.value) {
            this.fileName = this.file.value.name.replace(/\.[^/.]+$/, '');
            this.setCompanyDocumentName();
        }
    }

    onFileRemoved(): void {
        this.file.setValue(null);
        this.fileName = '';
        this.setCompanyDocumentName();
    }

    getExistingSelectedTags(): DocumentTag[] {
        return this.tagAutocomplete.getSelected().filter((tag: DocumentTag) => tag.isPersisted) as DocumentTag[];
    }

    getNewTags(): DocumentTag[] {
        return this.tagAutocomplete
            .getSelected()
            .filter((tag: DocumentTag) => !tag.isPersisted)
            .map((newTag: Chippable): DocumentTag => new DocumentTag({ name: newTag.chipLabel }));
    }

    onCosignersChange(): void {
        this.setCosignersLabel();
    }

    private setCosignersLabel(): void {
        let myKey: { key: string; count: number };
        const keyPrefix = this.companyDocument.isOfferLetter()
            ? 'document.signersLabel.candidate'
            : 'document.signersLabel.employee';
        if (this.cosigners > 1) {
            myKey = { key: `${keyPrefix}Other`, count: this.cosigners };
        } else if (this.cosigners === 1) {
            myKey = { key: `${keyPrefix}One`, count: 1 };
        } else {
            myKey = { key: `${keyPrefix}Zero`, count: 0 };
        }
        this.cosignersLabel = myKey;
    }

    private setCompanyDocumentName(): void {
        this.companyDocument.name = this.fileName;
    }
}
